/**
 * One facade over the two portal services.
 *
 * Rule: when a JWT is present, use the member service for everything (so runs and questions are
 * attributed to the person); anonymous visitors use the public service. Run actions follow the mode
 * the run was STARTED in (an anonymous run keeps using its run token even after the person signs in).
 */
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useProceduresServices,
  type ProcedureChatMessageView,
  type ProcedureChatReplyView,
  type ProcedureChatRequest,
  type ProcedurePortalView,
  type ProcedureRunFinishRequest,
  type ProcedureRunStepUpdateRequest,
  type ProcedureRunView,
  type ProceduresServices,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { proceduresKeys, type PortalMode } from './queryKeys';
import { getViewSnapshot, saveViewSnapshot } from './offlineStore';
import { getRunSnapshot, getRunToken, saveRunSnapshot } from './runStore';
import { isNetworkError } from './errors';

export type { PortalMode };

/** Thrown when an anonymous run has no token on this phone any more. */
export class MissingRunTokenError extends Error {
  readonly errors = ['run_token_invalid'];
  readonly status = 403;
  constructor() {
    super('run_token_invalid');
    this.name = 'MissingRunTokenError';
  }
}

function tokenFor(runId: number): string {
  const token = getRunToken(runId);
  if (!token) throw new MissingRunTokenError();
  return token;
}

/** Run actions for a given mode. Pure functions over the SDK services (also used by the sync engine). */
export function runActions(services: ProceduresServices, mode: PortalMode) {
  const { publicProcedures, proceduresMember } = services;
  return {
    getRun: (runId: number): Promise<ProcedureRunView> =>
      mode === 'member' ? proceduresMember.getMemberRun(runId) : publicProcedures.getPublicRun(runId, tokenFor(runId)),
    updateStep: (runId: number, stepId: number, req: ProcedureRunStepUpdateRequest): Promise<ProcedureRunView> =>
      mode === 'member'
        ? proceduresMember.updateMemberRunStep(runId, stepId, req)
        : publicProcedures.updatePublicRunStep(runId, tokenFor(runId), stepId, req),
    complete: (runId: number, req: ProcedureRunFinishRequest = {}): Promise<ProcedureRunView> =>
      mode === 'member' ? proceduresMember.completeMemberRun(runId, req) : publicProcedures.completePublicRun(runId, tokenFor(runId), req),
    abandon: (runId: number, req: ProcedureRunFinishRequest = {}): Promise<ProcedureRunView> =>
      mode === 'member' ? proceduresMember.abandonMemberRun(runId, req) : publicProcedures.abandonPublicRun(runId, tokenFor(runId), req),
  };
}

/** Current portal mode from the session: member when signed in, public otherwise. */
export function usePortalMode(): { mode: PortalMode; isInitializing: boolean; userId: string | null } {
  const { isAuthenticated, isInitializing, user } = useEnrichedAuth();
  return { mode: isAuthenticated ? 'member' : 'public', isInitializing, userId: user?.userId ?? null };
}

export interface ProcedureViewResult {
  view: ProcedurePortalView;
  /** True when this came from the copy on the phone because the network failed. */
  offline: boolean;
  savedAt: number | null;
}

/**
 * The published procedure. Network first; on a network failure the copy on this phone answers.
 * Refetches every 45 minutes while open so the signed media links (about 1 h) never go stale.
 */
export function useProcedureView(code: string | undefined) {
  const services = useProceduresServices();
  const { mode, isInitializing } = usePortalMode();
  const enabled = !!code && !isInitializing;
  return useQuery<ProcedureViewResult>({
    queryKey: proceduresKeys.view(mode, code ?? ''),
    enabled,
    queryFn: async () => {
      const c = code as string;
      try {
        const view =
          mode === 'member'
            ? await services.proceduresMember.getMemberProcedure(c)
            : await services.publicProcedures.getPublicProcedure(c);
        saveViewSnapshot(mode, c, view);
        return { view, offline: false, savedAt: Date.now() };
      } catch (err) {
        if (isNetworkError(err)) {
          const saved = getViewSnapshot(mode, c);
          if (saved) return { view: saved.view, offline: true, savedAt: saved.savedAt };
        }
        throw err;
      }
    },
    staleTime: 10 * 60 * 1000,
    refetchInterval: 45 * 60 * 1000,
    refetchOnWindowFocus: true,
    // Back in range after answering from the saved copy: fetch the latest straight away.
    refetchOnReconnect: 'always',
    retry: (count, err) => count < 1 && isNetworkError(err),
  });
}

/**
 * The run as the server last saw it. Falls back to the copy on this phone when offline, so the
 * runner always opens. Pending ticks are laid over it by the runner (see lib/runMerge.ts).
 */
export function useRunView(runId: number | null, mode: PortalMode | null) {
  const services = useProceduresServices();
  return useQuery<ProcedureRunView | null>({
    queryKey: proceduresKeys.run(runId ?? 0),
    enabled: !!runId && !!mode,
    queryFn: async () => {
      const id = runId as number;
      try {
        const view = await runActions(services, mode as PortalMode).getRun(id);
        saveRunSnapshot(view);
        return view;
      } catch (err) {
        if (isNetworkError(err)) return getRunSnapshot(id);
        throw err;
      }
    },
    initialData: () => (runId ? getRunSnapshot(runId) ?? undefined : undefined),
    initialDataUpdatedAt: 0,
    staleTime: 30 * 1000,
    refetchOnReconnect: 'always',
    retry: (count, err) => count < 1 && isNetworkError(err),
  });
}

/** Ask the assistant and read the conversation, in the right mode. */
export function useChatApi(code: string, mode: PortalMode) {
  const services = useProceduresServices();
  const queryClient = useQueryClient();
  const ask = useCallback(
    (req: ProcedureChatRequest, runToken?: string | null): Promise<ProcedureChatReplyView> =>
      mode === 'member'
        ? services.proceduresMember.askMember(code, req)
        : services.publicProcedures.askPublic(code, req, runToken ?? undefined),
    [services, code, mode],
  );
  const history = useCallback(
    (sessionKey: string, deviceId: string): Promise<ProcedureChatMessageView[]> =>
      mode === 'member'
        ? services.proceduresMember.getMemberChatHistory(code, sessionKey)
        : services.publicProcedures.getPublicChatHistory(code, sessionKey, deviceId),
    [services, code, mode],
  );
  return useMemo(() => ({ ask, history, queryClient }), [ask, history, queryClient]);
}
